// Report audits: figure coherence, regulatory compliance article by article and
// consistency across years, recomputed by the backend from the latest drafts.
// A KO row opens the section concerned; drafting it again reruns the audits.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { api } from "../api/client";
import { useResource } from "../api/resource";
import type { AuditKind, AuditResult, AuditRow } from "../api/types";
import { formatAmount } from "../components/drafting";
import { EmptyState, ErrorState, Notice, PageHeader, SkeletonRows, StatusBadge } from "../components/ui";
import { keys, useDossier } from "../dossier";
import { formatDateTime, locale, w } from "../i18n";
import type { UserStatus } from "../status";

const TABS: AuditKind[] = ["coherence", "regulatory", "vintage"];
const RESULT_STATUS: Record<AuditResult, UserStatus> = { ok: "ready", ko: "failed", review: "needs_review", na: "not_started" };
const FILTERS: (AuditResult | "all")[] = ["all", "ko", "review", "ok", "na"];

export function ResultBadge({ result }: { result: AuditResult }) {
  return <StatusBadge status={RESULT_STATUS[result]} label={w(`audits.result.${result}`)} />;
}

function sectionPath(dossierId: string, code: string) {
  return code.includes(".") ? `/dossiers/${dossierId}/sections/${code}` : `/dossiers/${dossierId}/chapters/${code}`;
}

export function AuditsPage() {
  const { dossier } = useDossier();
  const navigate = useNavigate();
  const lang = locale();
  const report = useResource(keys.reportAudits(dossier.id, lang), () => api.reportAudits(dossier.id, lang));
  const [tab, setTab] = useState<AuditKind>("coherence");
  const [filter, setFilter] = useState<AuditResult | "all">("all");
  const data = report.data;
  const rows = (data?.audits[tab] || []).filter(row => filter === "all" || row.result === filter);
  const all = data?.summary.all;
  const open = (row: AuditRow) => row.section && navigate(sectionPath(dossier.id, row.section));

  return <>
    <PageHeader title={w("audits.title")} lead={w("audits.lead")}
      actions={<a className="ws-btn" href={api.reportAuditsExportUrl(dossier.id, lang)} download>
        <Download size={16} aria-hidden="true" />{w("audits.export")}</a>} />
    {report.error && <ErrorState title={w("common.genericError")} error={report.error} onRetry={report.reload} />}

    <section className="ws-card ws-steps ws-steps-5" aria-label={w("audits.summary")}>
      {(["total", "ok", "ko", "review", "na"] as const).map(key => <div key={key} className="ws-step">
        <div className="ws-step-label">{w(`audits.kpi.${key}`)}</div>
        <div className={`ws-step-value${key === "ko" && all?.ko ? " ws-negative" : ""}`}>{all ? all[key] : "…"}</div>
      </div>)}
    </section>
    {data && <p className="ws-small ws-muted" style={{ margin: "8px 0 0" }}>
      {w("audits.basis", { count: data.sections_drafted, at: formatDateTime(data.generated_at) })}</p>}

    <div className="ws-tabs" role="tablist" style={{ marginTop: 16 }}>{TABS.map(item => {
      const counts = data?.summary[item];
      return <button key={item} role="tab" className="ws-tab" aria-selected={tab === item} onClick={() => setTab(item)}>
        {w(`audits.tab.${item}`)} ({counts?.total ?? 0})
        {counts?.ko ? <span className="ws-tab-badge">{w("audits.koCount", { count: counts.ko })}</span> : null}
      </button>;
    })}</div>

    <div className="ws-row" style={{ marginBottom: 12 }}>
      <label className="ws-small ws-muted" htmlFor="f-result">{w("audits.col.result")}</label>
      <select id="f-result" className="ws-select" value={filter} onChange={e => setFilter(e.target.value as AuditResult | "all")}>
        {FILTERS.map(f => <option key={f} value={f}>{f === "all" ? w("docs.filter.all") : w(`audits.result.${f}`)}</option>)}
      </select>
    </div>

    {tab === "vintage" && data && !data.vintage.published_report &&
      <div style={{ marginBottom: 12 }}><Notice tone="neutral">{w("audits.noPublished")}</Notice></div>}

    <div className="ws-card ws-table-wrap"><table className="ws-table ws-audit-table">
      <thead><tr>{columns(tab).map(c => <th key={c}>{w(`audits.col.${c}`)}</th>)}<th><span className="ws-sr-only">{w("issues.col.action")}</span></th></tr></thead>
      <tbody>
        {report.loading && <SkeletonRows columns={columns(tab).map(() => "20%")} />}
        {rows.map(row => <tr key={row.id} className={row.section ? "ws-clickable" : undefined}
          onClick={() => open(row)}>
          {cells(row)}
          <td style={{ textAlign: "right" }}>{row.section &&
            <Link className="ws-btn sm" to={sectionPath(dossier.id, row.section)} onClick={e => e.stopPropagation()}>{w("common.open")}</Link>}</td>
        </tr>)}
      </tbody></table>
      {data && !rows.length && <EmptyState title={w(filter === "all" ? `audits.empty.${tab}` : "audits.emptyFilter")} />}
    </div>

    <div style={{ marginTop: 16 }}><Notice tone="neutral">{w("audits.note")}</Notice></div>
  </>;
}

function columns(tab: AuditKind): string[] {
  if (tab === "coherence") return ["aggregate", "appearances", "result", "comment"];
  if (tab === "regulatory") return ["reference", "section", "requirement", "result", "comment"];
  return ["indicator", "section", "current", "published", "difference", "result", "comment"];
}

function cells(row: AuditRow) {
  if (row.audit === "coherence") return <>
    <td><strong>{row.label}</strong></td>
    <td><ul className="ws-appearances">{(row.appearances || []).map((a, index) =>
      <li key={index} className={a.matches ? undefined : "ws-negative"}>
        <span className="ws-mono">{a.kind === "source" ? (a.location || "QRT") : `${a.section} (${w("audits.text")})`}</span> : {a.value}
      </li>)}</ul></td>
    <td><ResultBadge result={row.result} /></td>
    <td className="ws-small">{row.comment}</td>
  </>;
  if (row.audit === "regulatory") return <>
    <td className="ws-mono">{row.reference}</td>
    <td className="ws-mono">{row.section}</td>
    <td>{row.item}{row.automated && <div className="ws-small ws-faint">{w("audits.automated")}</div>}</td>
    <td><ResultBadge result={row.result} /></td>
    <td className="ws-small">{row.comment}
      {row.excerpt && <details onClick={e => e.stopPropagation()}><summary>{w("audits.excerpt")}</summary>
        <blockquote className="ws-quote">{row.excerpt}</blockquote></details>}</td>
  </>;
  const value = (v: number | null | undefined) => v == null ? w("common.notAvailable") : formatAmount(v, row.unit);
  return <>
    <td><strong>{row.label}</strong></td>
    <td className="ws-mono">{row.section || ""}</td>
    <td className="ws-num">{value(row.current)}</td>
    <td className="ws-num">{value(row.published)}
      {row.unit_assumed && <div className="ws-small ws-faint">{w("audits.unitAssumed")}</div>}</td>
    <td className="ws-num">{row.difference == null ? "" : `${value(row.difference)}${row.difference_pct != null && row.unit !== "%"
      ? ` (${formatAmount(row.difference_pct, "%")})` : ""}`}</td>
    <td><ResultBadge result={row.result} /></td>
    <td className="ws-small">{row.comment}</td>
  </>;
}
